import { useState } from "react";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import { i18n } from "@frontend/i18n";

type FlowStateType =
  | "idle"
  | "sending"
  | "codeSent"
  | "validating"
  | "verified"
  | "error";

const VerificationStatus = {
  RATE_LIMITED: "rate_limited",
  VERIFIED: "verified",
} as const;

interface EmailVerificationFlowControllerReturn {
  readonly state: {
    readonly flowState: FlowStateType;
    readonly code: string;
    readonly errorMessage: string;
  };
  readonly actions: {
    readonly setCode: (code: string) => void;
    readonly handleSend: () => void;
    readonly handleValidate: () => void;
  };
}

export function useEmailVerificationFlowController(): EmailVerificationFlowControllerReturn {
  const [flowState, setFlowStateType] = useState<FlowStateType>("idle");
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const {
    sendVerificationEmail: sendMutation,
    validateVerificationCode: validateMutation,
    refresh,
  } = useUserActions();

  const handleSend = (): void => {
    setFlowStateType("sending");
    setErrorMessage("");
    sendMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (data.status === VerificationStatus.RATE_LIMITED) {
          setFlowStateType("error");
          setErrorMessage(i18n.t("user.account.settings.rateLimited"));
        } else {
          setFlowStateType("codeSent");
        }
      },
      onError: () => {
        setFlowStateType("error");
        setErrorMessage(i18n.t("user.account.settings.verifyError"));
      },
    });
  };

  const handleValidate = (): void => {
    if (code.trim() === "") {
      return;
    }
    setFlowStateType("validating");
    setErrorMessage("");
    validateMutation.mutate(code.trim(), {
      onSuccess: (data) => {
        if (data.status === VerificationStatus.VERIFIED) {
          setFlowStateType("verified");
          void refresh();
        } else {
          setFlowStateType("error");
          setErrorMessage(i18n.t("user.account.settings.verifyError"));
        }
      },
      onError: () => {
        setFlowStateType("error");
        setErrorMessage(i18n.t("user.account.settings.verifyError"));
      },
    });
  };

  return {
    state: { flowState, code, errorMessage },
    actions: { setCode, handleSend, handleValidate },
  };
}
